import * as React from 'react';

import { TrackCallbackType } from '@blockworks/platform/services';
import { ImageWithFallback } from '@blockworks/ui';

import { Link } from '@/components/common';
import SecondaryButton from '@/components/display/buttons/secondary-button';
import { OpenIcon } from '@/components/pages/token-transparency/components/open-icon';
import { tokenTransparencyBounds } from '@/components/pages/token-transparency/token-transparency.model';
import type { TokenTransparencyWithTotals } from '@/components/pages/token-transparency/token-transparency-page-static-props';
import { calculateTotalTransparencyScore } from '@/components/pages/token-transparency/utils/calculate-total-transparency-score';
import { determineScoreClasses } from '@/components/pages/token-transparency/utils/determine-score-classes';

type TokenTransparencyTableRowProps = {
    tt: TokenTransparencyWithTotals;
    track: TrackCallbackType;
    assets: { [key: string]: { imageUrl: string } };
};

const CssClasses = {
    mobileHiddenCell:
        'hidden md:table-cell px-1 py-3 place-items-center align-center text-center content-center place-items-center',
    standardCenteredCell:
        'xs:table-cell py-3 place-items-center align-center text-center content-center place-items-center',
    standardCell: 'xs:table-cell px-2 py-3',
};

export const TokenTransparencyTableRow = (props: TokenTransparencyTableRowProps) => {
    const { tt, assets, track } = props;

    if (!tt.tokenSymbol || !assets[tt.tokenSymbol]) {
        console.warn(`Asset image not found for token symbol: ${tt.tokenSymbol}`);
        return null;
    }

    if (!tt.filingReportUrl) {
        console.warn(`Filing report URL not found for token symbol: ${tt.tokenSymbol}`);
        return null;
    }

    return (
        <tr key={tt.tokenSymbol} className="group hover:bg-gray-50">
            <td className="pl-4 pr-2 md:pl-8 py-3 min-w-24 border-r">
                <div className="flex justify-between">
                    <div className="flex justify-start flex-nowrap items-center self-stretch relative gap-2">
                        <ImageWithFallback
                            src={assets[tt.tokenSymbol]?.imageUrl ?? ''}
                            className="w-[25px] h-[25px] rounded-full"
                            alt={tt.tokenSymbol ?? ''}
                            fallbackSrc="/images/Blockworks_Meta_Logo.png"
                            width={25}
                            height={25}
                        />

                        <div className="truncate text-sm flex flex-col justify-start items-start self-stretch relative gap-0">
                            <Link
                                href={`/price/${tt.tokenAssetId ?? tt.tokenSymbol?.toLowerCase()}`}
                                className="text-brand hover:text-black"
                            >
                                {tt.projectName}
                            </Link>
                            <span className="text-xxs text-gray-500">{tt.tokenSymbol}</span>
                        </div>
                    </div>
                </div>
            </td>
            <td className={CssClasses.mobileHiddenCell}>
                <div className="flex justify-center items-center h-full">
                    <Link
                        className={determineScoreClasses(
                            tt.projectAndTeamRating ?? 0,
                            tokenTransparencyBounds.projectAndTeamRating,
                        )}
                        href={tt.filingReportUrl ?? ''}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => track('TokenTransparency.ClickedProjectTeamRating', tt)}
                    >
                        <span>
                            {tt.projectAndTeamRating ?? 'N/A'}/{tokenTransparencyBounds.projectAndTeamRating.max}
                        </span>
                    </Link>
                </div>
            </td>

            <td className={CssClasses.mobileHiddenCell}>
                <div className="flex justify-center items-center h-full">
                    <Link
                        className={determineScoreClasses(
                            tt.tokenSupplyAndAllocationRating ?? 0,
                            tokenTransparencyBounds.tokenSupplyAndAllocationRating,
                        )}
                        href={tt.filingReportUrl ?? ''}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => track('TokenTransparency.ClickedTokenSupplyRating', tt)}
                    >
                        <span>
                            {tt.tokenSupplyAndAllocationRating ?? 'N/A'}/
                            {tokenTransparencyBounds.tokenSupplyAndAllocationRating.max}
                        </span>
                    </Link>
                </div>
            </td>

            <td className={CssClasses.mobileHiddenCell}>
                <div className="flex justify-center items-center h-full">
                    <Link
                        className={determineScoreClasses(
                            tt.transactionsAndMarketStructureRating ?? 0,
                            tokenTransparencyBounds.transactionsAndMarketStructureRating,
                        )}
                        href={tt.filingReportUrl ?? ''}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => track('TokenTransparency.ClickedTxMarketStructureRating', tt)}
                    >
                        <span>
                            {tt.transactionsAndMarketStructureRating ?? 'N/A'}/
                            {tokenTransparencyBounds.transactionsAndMarketStructureRating.max}
                        </span>
                    </Link>
                </div>
            </td>

            <td className={CssClasses.mobileHiddenCell}>
                <div className="flex justify-center items-center h-full">
                    <Link
                        className={determineScoreClasses(
                            tt.financialDisclosureRating ?? 0,
                            tokenTransparencyBounds.financialDisclosureRating,
                        )}
                        rel="noopener noreferrer"
                        href={tt.filingReportUrl ?? ''}
                        target="_blank"
                        onClick={() => track('TokenTransparency.ClickedFinDisclosureRating', tt)}
                    >
                        <span>
                            {tt.financialDisclosureRating ?? 'N/A'}/
                            {tokenTransparencyBounds.financialDisclosureRating.max}
                        </span>
                    </Link>
                </div>
            </td>

            <td className={`${CssClasses.standardCenteredCell} border-l border-r bg-gray-50`}>
                <div className="flex justify-center items-center h-full">
                    <Link
                        className={determineScoreClasses(
                            calculateTotalTransparencyScore(tt),
                            tokenTransparencyBounds.total,
                        )}
                        href={tt.filingReportUrl ?? ''}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => track('TokenTransparency.ClickedTotalRating', tt)}
                    >
                        <span>
                            {tt.total}/{tokenTransparencyBounds.total.max}
                        </span>
                    </Link>
                </div>
            </td>

            <td className={`${CssClasses.standardCenteredCell}`}>
                <SecondaryButton
                    link={tt.filingReportUrl ?? ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track('TokenTransparency.ClickedOpenFiling', tt)}
                    className="inline-flex gap-1 items-center border bg-white px-4 py-1.5 text-xs font-normal text-gray-700 hover:bg-gray-50 focus:outline-none"
                >
                    <OpenIcon />
                    Open
                </SecondaryButton>
            </td>
        </tr>
    );
};
